// All user-facing Slovenian text (SPEC §8.5). Functions take already formatted numbers.

import type { Smer } from '../engine/types';

type Case = 'nom' | 'gen' | 'loc';
const SMER_ADJ: Record<Smer, Record<Case, string>> = {
  'S (sever)': { nom: 'severna', gen: 'severne', loc: 'severni' },
  'V (vzhod)': { nom: 'vzhodna', gen: 'vzhodne', loc: 'vzhodni' },
  'J (jug)': { nom: 'južna', gen: 'južne', loc: 'južni' },
  'Z (zahod)': { nom: 'zahodna', gen: 'zahodne', loc: 'zahodni' },
};

// the planned house has building number 0 (engine/plan.ts NEW_ST); cadastral buildings are "stavba 260"
const bGen = (st: number) => (st === 0 ? 'nove hiše' : `stavbe ${st}`);
const bNom = (st: number) => (st === 0 ? 'nova hiša' : `stavba ${st}`);
const bAcc = (st: number) => (st === 0 ? 'novo hišo' : `stavbo ${st}`);
const cap1 = (x: string) => x[0].toUpperCase() + x.slice(1);

export const sl = {
  lang: 'sl',
  appName: 'Sončnik',
  smerAdj: (s: Smer, c: Case) => SMER_ADJ[s][c],
  /** Short building label for tables and map labels. */
  bShort: (st: number) => (st === 0 ? 'nova' : String(st)),

  // landing
  landing: {
    eyebrow: 'Študija osončenosti',
    title: 'Koliko sonca dobi vaša parcela?',
    lead: 'Vpišite katastrsko občino in številko parcele. Izračunamo sonce in senco na parceli in strehah ter oceno za sončno elektrarno.',
    ko: 'Katastrska občina (k.o.)',
    koPlaceholder: 'npr. 1131 Marija Dobje',
    parcel: 'Številka parcele',
    parcelPlaceholder: 'npr. 243/1',
    submit: 'Izračunaj',
    coverage: (names: string) => `Pokrito območje: občina ${names}.`,
    example: 'Primer: ',
    mapPick: 'Izberi parcelo na karti',
    mapHide: 'Skrij karto',
    mapHint: 'Približajte in kliknite parcelo.',
    mapLoading: 'Nalagam karto …',
    mapFail: 'Karte ni bilo mogoče naložiti.',
    mapSelected: (st: string, ko: string) => `Parcela ${st}, k.o. ${ko}`,
    mapGo: 'Izračunaj za to parcelo',
    mapAttribution: '© GURS kataster',
    address: 'Naslov',
    addressPlaceholder: 'npr. Marija Dobje 29',
    addressNone: 'Ni zadetkov v pokritem območju.',
    addressNoParcel: 'Pod tem naslovom ni bilo mogoče najti parcele.',
    addressLoading: 'Iščem naslov …',
    or: 'ali po parcelni številki',
    free: 'Brezplačno, brez registracije.',
  },

  // progress
  steps: {
    find: 'Iščem parcelo',
    dsm: 'Nalagam model površja',
    horizon: 'Računam obzorje',
    irradiance: 'Računam sevanje',
    report: 'Pripravljam poročilo',
  },
  progressTitle: (st: string, ko: string) => `Parcela ${st}, k.o. ${ko}`,

  // errors (SPEC §8.6)
  err: {
    koUnknown: 'Katastrska občina ni v pokritem območju.',
    koCovered: 'Pokrite katastrske občine:',
    parcelUnknown: (p: string, ko: string) => `Parcela ${p} v k.o. ${ko} ne obstaja.`,
    outside: 'Območje še ni pokrito.',
    outsideLead: 'Pustite e-pošto in sporočili vam bomo, ko bo vaše območje na voljo.',
    tooLarge: 'Parcela je prevelika za brezplačno analizo (največ 600 × 600 m).',
    fetch: 'Podatkov ni bilo mogoče naložiti.',
    nodata: 'Za to parcelo ni podatkov o površju.',
    generic: 'Pri izračunu je prišlo do napake.',
    retry: 'Poskusi znova',
    back: 'Nova parcela',
    noWebgl: '3D pogled v tem brskalniku ni na voljo (WebGL ni podprt).',
  },

  // report header
  eyebrow: 'Študija osončenosti',
  title: (st: string, ko: number, koName: string) => `Parcela ${st}, k.o. ${ko} ${koName}`,
  meta: {
    obcina: 'Občina',
    area: 'Površina parcele',
    buildings: 'Katastrska št. stavbe',
    center: 'Center D96/TM',
    wgs: 'Koordinate',
    elev: 'Nadmorska višina',
    lidar: 'Model površja',
    lidarVal: 'LiDAR 1 m (ARSO)',
    none: 'brez stavb',
  },
  dataLine: (cadastreDate: string) => `Stanje javnih podatkov: ${cadastreDate}`,
  grid2m: 'Izračun v mreži 2 m',
  actions: { copy: 'Kopiraj povezavo', copied: 'Povezava kopirana', print: 'Natisni / PDF', pdf: 'PDF na e-pošto', newParcel: 'Nova parcela' },

  // facts
  facts: {
    open: 'sončno obsevanje lokacije brez ovir',
    ground: (pct: string) => `povprečje na vrtu in dvorišču (−${pct} % zaradi senc)`,
    winter: 'ur sonca na vrtu na najkrajši zimski dan (21. 12.)',
    winterSub: (astro: string) => `ur sonca na vrtu pozimi (21. 12. · od ${astro} h možnih)`,
    pv: (smerLoc: string, st: number, kwh: string) => `elektrarna na ${smerLoc} strešini ${bGen(st)} (~${kwh} kWh/leto)`,
    pvSelected: (n: number, kwh: string) => `elektrarna na ${n === 1 ? 'izbrani strešini' : `${n} izbranih strešinah`} (~${kwh} kWh/leto)`,
    summer: 'ur sonca na vrtu poleti (21. 6.)',
    roofBest: 'letno obsevanje na najboljši strešini',
    astro: 'največ možnih ur sonca pozimi (21. 12.)',
  },

  // findings (SPEC §6.2)
  find: {
    horizonOpen: (min: string, max: string, north: string) => `Lega je odprta proti jugu — okoliški hribi so nizki (${min}–${max}°), zato pozimi skoraj ne jemljejo sonca (proti severu do ${north}°).`,
    horizonHigh: (max: string) => `Okoliški hribi proti jugu segajo do ${max}° visoko, kar pozimi skrajša sončni dan.`,
    shadeTrees: (pct: string) => `Glavni vir sence na parceli so drevesa (krošnje pokrivajo okoli ${pct} % površine) in stavbe.`,
    shadeFew: 'Na parceli je malo dreves; senco povzročajo predvsem stavbe in okoliški teren.',
    seasons: (ge6: string, ge4: string) => `Spomladi in jeseni (21. 3.) ima ${ge6} % vrta vsaj 6 ur direktnega sonca, pozimi (21. 12.) pa ima vsaj 4 ure sonca še ${ge4} % vrta.`,
    roofBest: (smerNom: string, st: number, tilt: string, az: string, loss: string) =>
      `Najboljša strešina za sončno elektrarno je ${smerNom} stran ${bGen(st)} (naklon ${tilt}°, usmerjenost ${az}°, izguba zaradi senc okolice ~${loss} %).`,
    roofSecond: (smerNom: string, st: number) => ` Druga primerna površina je ${smerNom} strešina ${bGen(st)}.`,
    roofNone: 'Nobena strešina ne dosega 1000 kWh/m² na vsaj 15 m².',
    noBuildings: 'Na parceli v katastru ni evidentiranih stavb.',
    noGround: 'Parcelo v celoti pokrivajo stavbe ali krošnje, zato podatkov za vrt ni; poročilo prikazuje strehe.',
  },

  // map (SPEC §6.3)
  map: {
    title: 'Karta osončenosti',
    alt: 'Karta osončenosti parcele',
    tabs: { annual: 'Letno obsevanje', sh1221: 'Zima (21. 12.)', sh0321: 'Pomlad / jesen (21. 3.)', sh0621: 'Poletje (21. 6.)', veg: 'Drevesa in ovire' },
    capAnnual: 'Letno sončno obsevanje vsakega kvadratnega metra (strehe, vrt, krošnje) z upoštevanjem naklona in senc. Rdeče in oranžne površine dobijo največ sonca, modre pa so v senci.',
    capWinter: (astro: string) => `Ure neposrednega sonca na najkrajši zimski dan (21. december) ob jasnem vremenu. Brez hribov in ovir bi sonce sijalo ${astro} ur.`,
    capEquinox: 'Ure neposrednega sonca spomladi (21. marec) in jeseni (23. september). Najboljše merilo za zasaditev vrta in bivalne terase.',
    capSummer: 'Ure neposrednega sonca na najdaljši poletni dan (21. junij).',
    capVeg: 'Zeleno so označena drevesa in grmovje iz laserskega snemanja (2014–15). Če je bilo drevo medtem posekano ali je zraslo, to upoštevajte pri ogledu.',
    capTail: 'Črtkana črta je meja parcele, številke označujejo stavbe. Kliknite katerokoli točko na karti za natančen potek sonca.',
    vegLegend: 'krošnje dreves in grmovja',
    north: 'S',
    scale: '20 m',
    tipAnnual: (v: string) => `${v} kWh/m² na leto`,
    tipHours: (v: string) => `${v} ur sonca`,
    tipDays: 'Ure sonca',
    tipRoof: (st: number) => `streha ${bGen(st)}`,
    tipVeg: 'krošnja drevesa',
    tipGround: 'vrt / odprta površina',
    tipOutside: 'okolica izven parcele',
    tipPick: 'Kliknite za prikaz ure vzhoda in zahoda na tej točki',
  },

  // sun path (SPEC §6.4)
  sunpath: {
    title: 'Kdaj posije sonce (pot sonca in ovire)',
    aria: 'Diagram poti sonca z obzorjem',
    tabGround: 'Na vrtu (sredina parcele)',
    tabRoof: (smerNom: string, st: number) => `${smerNom[0].toUpperCase()}${smerNom.slice(1)} streha (${st === 0 ? bGen(st) : `stavba ${st}`})`,
    tabPicked: 'Izbrana točka na karti',
    windowSummary: (winterSpan: string, summerSpan: string) => `Ob jasnem vremenu posije sonce na to točko pozimi (21. 12.) ${winterSpan}, poleti (21. 6.) pa ${summerSpan}.`,
    noSunWinter: 'zaradi sence manj kot pol ure',
    legend: 'polna črta: sonce sije neposredno · črtkano: sonce zakrito za hribom, stavbo ali drevesom · pike: polne ure',
    capGround: (kwh: string) => `Tipična točka na odprtem delu parcele (letno ~${kwh} kWh/m²). Sivi pas spodaj prikazuje okoliške hribe, stavbe in drevesa, ki zakrivajo pogled proti nebu.`,
    capRoof: (smerGen: string, st: number, kwh: string) => `Sredina ${smerGen} strešine ${bGen(st)} (letno ~${kwh} kWh/m²). Sivi pas spodaj prikazuje ovire v okolici, kot jih vidi ta streha.`,
    capPicked: (e: string, n: string, kwh: string) => `Točka, ki ste jo kliknili na karti (E ${e} · N ${n}, letno ~${kwh} kWh/m²). Sivi pas prikazuje ovire v okolici.`,
    days: { jun: '21. jun. (poletje)', mar: '21. mar. / 23. sep.', feb: '21. feb. / 21. okt.', dec: '21. dec. (zima)' },
    tableCaption: 'Obzorje po straneh neba',
    az: 'Smer neba (azimut)',
    h: 'Višina ovire',
  },

  // monthly
  monthly: {
    title: 'Sončna energija skozi leto',
    aria: 'Mesečno sevanje',
    open: 'teoretično brez hribov in senc',
    ground: 'dejansko na vrtu vaše parcele',
    unit: 'kWh/m²',
    months: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
    monthNames: ['januar', 'februar', 'marec', 'april', 'maj', 'junij', 'julij', 'avgust', 'september', 'oktober', 'november', 'december'],
    month: 'Mesec',
  },

  // sun hours table
  days: {
    title: 'Ure sonca na vrtu po letnih časih',
    cols: ['Letni čas', 'Brez ovir', 'Povprečje na vrtu', 'Vsaj 2 uri', 'Vsaj 4 ure', 'Vsaj 6 ur'],
    dayLabels: {
      '21. 12.': 'Zima (21. 12.)',
      '21. 2.': 'Zgodnja pomlad / jesen (21. 2.)',
      '21. 3.': 'Pomlad / jesen (21. 3.)',
      '21. 6.': 'Poletje (21. 6.)',
    } as Record<string, string>,
    cap: 'Ure neposrednega sonca ob jasnem dnevu za vrt in dvorišče (brez streh in krošenj). Stolpci »Vsaj 2/4/6 ur« kažejo, kolikšen delež vrta (%) dobi toliko ur sonca.',
    noGround: 'Parcela nima odprtih površin, zato so prikazane le najdaljše možne ure sonca.',
  },

  // roofs
  roofs: {
    title: 'Strehe in sončna elektrarna',
    cols: ['Stavba', 'Smer neba', 'Površina (m²)', 'Naklon', 'Usmerjenost', 'Osončenost (kWh/m²)', 'Izguba zaradi senc', 'Primernost'],
    cap: 'Površine, nakloni in usmerjenost (180° = jug, 90° = vzhod, 270° = zahod) so izmerjeni iz 3D modela. Izguba zaradi senc pove, kolikšen delež sonca strehi odvzamejo okoliški hribi, drevesa ali sosednje stavbe.',
    pvUnit: 'kWh/kWp',
    pvLine: (kwp: string, m2: string, kwh: string) => `~${kwp} kWp na ${m2} m² · ~${kwh} kWh/leto`,
    pvName: (st: number, smerNom: string) => `${cap1(bNom(st))}, ${smerNom} strešina`,
    noPv: 'Nobena strešina nima vsaj 15 m² dovolj osončene površine (nad 1000 kWh/m²), zato sončne elektrarne na strehi ne priporočamo.',
    selectCol: 'Izberi',
    selectRow: (st: number, smerNom: string) => `Izberi ${bAcc(st)}, ${smerNom} strešino za izračun`,
    selectHint: '💡 Namig: V tabeli označite eno ali več strešin (npr. južno in zahodno stran), na katere želite postaviti sončno elektrarno. Spodnji izračun in prihranek se bosta samodejno prilagodila vaši izbiri.',
    selected: (n: number) => `Za izračun ${n === 1 ? 'je izbrana 1 strešina' : `so izbrane ${n} strešine`}.`,
    toCalc: 'Skoči na izračun prihranka',
    clear: 'Ponastavi izbiro strešin',
  },

  // CTA (SPEC §8.3)
  cta: {
    title: 'Želite ponudbo za sončno elektrarno na tej strehi?',
    lead: 'Povpraševanje je brezplačno in neobvezujoče. Preverjen izvajalec prejme tehnične podatke vaše strehe iz tega poročila, zato vam ne bo treba ničesar razlagati od začetka.',
    titleKwp: (kwp: string) => `Brezplačna ponudba za ~${kwp} kWp sončno elektrarno`,
    next: ['Vpišete ime in e-pošto (30 sekund).', 'Izvajalec prejme izmerjene podatke vaše strehe iz poročila.', 'V 2 delovnih dneh prejmete okvirno ponudbo.'],
    nextTitle: 'Kako poteka',
    more: '+ Dodaj telefon ali opombo',
    name: 'Ime in priimek',
    email: 'E-poštni naslov',
    phone: 'Telefonska številka',
    note: 'Opomba (npr. želja po hranilniku)',
    optional: '(neobvezno)',
    consent: 'Strinjam se, da moje kontaktne podatke in izračun strehe posredujete preverjenemu izvajalcu za pripravo ponudbe.',
    submit: 'Pridobi brezplačno ponudbo',
    sending: 'Pošiljam …',
    ok: 'Hvala! Izvajalec vas bo kontaktiral v 2 delovnih dneh.',
    fail: (email: string) => `Pošiljanje ni uspelo. Poskusite znova ali nam pišite na ${email}.`,
    sticky: 'Pridobi ponudbo',
    badEmail: 'Vpišite veljaven e-poštni naslov.',
    required: 'Obvezno polje.',
    waitlistSubmit: 'Obvestite me',
    waitlistOk: 'Hvala. Obvestili vas bomo, ko bo območje pokrito.',
    verdictButton: (kwp: string) => `Pridobite brezplačno ponudbo za ~${kwp} kWp`,
    captcha: 'Preverjanje ni uspelo. Osvežite stran in poskusite znova.',
  },

  // PV system and savings estimate (all assumptions printed, main ones editable)
  savings: {
    title: 'Izračun sončne elektrarne in prihranka',
    lead: 'Izračun temelji na trenutnih cenah elektrike v Sloveniji. Spodaj lahko s klikom izberete drugo velikost elektrarne, vključite subvencijo ali prilagodite svojo letno porabo.',
    option: 'Izbira velikosti elektrarne',
    matched: 'prilagojeno vaši porabi',
    matchedChip: (kwh: string) => `Prilagojeno porabi (${kwh} kWh)`,
    matchedLine: (kwp: string, kwh: string, full: string) => `Elektrarna moči ~${kwp} kWp letno proizvede približno toliko elektrike, kot jo vaše gospodinjstvo porabi (${kwh} kWh). Ker se viški oddane elektrike odkupujejo po nizki ceni, se takšna elektrarna povrne hitreje kot polna zasedenost strehe (~${full} kWp).`,
    sysPlane: 'Izbrana strešina',
    sysPlanes: 'Izbrane strešine',
    sysArea: 'Skupna površina strešin',
    selChip: (n: number) => (n === 1 ? 'Izbrana strešina (1)' : `Izbrane strešine (${n})`),
    selLine: 'Izračun za strešine, ki ste jih označili v tabeli zgoraj (solarni paneli pokrivajo 70 % površine vsake izbrane strešine).',
    selHint: 'Želite izračun za drugo kombinacijo strešin (npr. jug + zahod)?',
    selHintLink: 'Označite jih v tabeli streh zgoraj',
    selWeak: (names: string) => `Opozorilo: ${names} prejme manj kot 1000 kWh/m² sonca letno, zato bo donosnost na tem delu strehe nižja.`,
    sysPower: 'Priporočena moč elektrarne',
    sysYield: 'Letni izplen na 1 kWp moči',
    sysP50: 'Pričakovana letna proizvodnja',
    sysP90: 'Proizvodnja v slabšem vremenskem letu',
    sysConn: 'Potrebne varovalke (priključna moč)',
    connVal: (a: number, kw: string) => `vsaj 3 × ${a} A (${kw} kW)`,
    connBig: 'nad 3 × 63 A (potrebno posebno soglasje)',
    connNote: (kw: string) => `Za priklop ~${kw} kW elektrarne potrebujete ustrezno priključno moč (preverite jo na računu za elektriko).`,
    monthlyTitle: (kwp: string) => `Pričakovana proizvodnja elektrike po mesecih za ~${kwp} kWp (v kWh)`,
    total: 'Skupaj leto',
    perYear: 'letni prihranek pri elektriki',
    payback: 'vračilna doba naložbe',
    years: (y: string) => `~${y} let`,
    noPayback: 'več kot 25 let',
    cost: 'okvirna investicija z DDV (brez subvencije)',
    costNet: 'okvirna investicija z DDV (po subvenciji)',
    net25: (y: number) => `čisti zaslužek v ${y} letih`,
    eur: (v: string) => `${v} €`,
    verdictLine: (kwp: string, eur: string, years: string) => `Za izbrano elektrarno ~${kwp} kWp znaša ocenjeni prihranek ~${eur} € na leto, investicija pa se povrne v ~${years} letih.`,
    verdictNoPayback: (kwp: string, eur: string) => `Za elektrarno ~${kwp} kWp znaša ocenjeni prihranek ~${eur} € na leto; brez subvencije ali hranilnika je vračilna doba daljša od 25 let.`,
    breakdown: (self: string, surplus: string) => `Od proizvedene elektrike boste doma porabili ~${self} kWh, v omrežje pa prodali ~${surplus} kWh viškov.`,
    loanLine: (p: string, y: number, r: string, m: string, s: string) => `Primer financiranja: ob kreditu ${p} na ${y} let (${r} obresti) znaša obrok ~${m}/mesec, vaš prihranek pri elektriki pa ~${s}/mesec.`,
    scTitle: 'Primerjava scenarijev (s subvencijo in baterijskim hranilnikom)',
    scCols: ['Scenarij', 'Investicija z DDV', 'Subvencija Borzen', 'Vaš strošek (neto)', 'Letni prihranek', 'Vračilna doba', 'Čisti dobiček v 25 letih'],
    scNone: 'Brez subvencije',
    scPv: 'Subvencija (brez hranilnika)',
    scBattery: 'Subvencija + baterijski hranilnik',
    scName: { none: 'brez subvencije', pv: 'subvencija brez hranilnika', battery: 'subvencija s hranilnikom' } as Record<'none' | 'pv' | 'battery', string>,
    scNote: 'Aktualna subvencija Borzen (JP-SO-02): do 250 €/kW (največ 25 % stroškov) za elektrarno brez hranilnika ter do 675 €/kW (največ 40 % stroškov) za elektrarno z baterijskim hranilnikom.',
    assumptions: 'Uporabljene predpostavke izračuna',
    assumptionsEdit: '⚙️ Prilagodi svojo porabo elektrike in cene',
    detailsMore: 'Prikaži vse tehnične predpostavke in tabelo po letih (1–25 let)',
    buy: 'Vaša cena elektrike z omrežnino in DDV (€/kWh)',
    sell: 'Odkupna cena oddanih viškov (€/kWh)',
    selfShare: 'Delež elektrike, ki jo porabite sproti doma (brez baterije običajno 30–40 %)',
    selfShareP: 'Delež elektrike, porabljen neposredno doma',
    maxCover: 'Največji delež letne porabe, pokrit brez baterije',
    consumption: 'Vaša letna poraba elektrike (kWh na leto)',
    costPerKwp: 'Okvirna cena izvedbe na ključ (€/kWp z DDV)',
    subsidy: 'V glavnih številkah upoštevaj subvencijo Borzen (250 €/kW)',
    subsidyP: 'Subvencija v glavnih številkah',
    subsidyOn: (perKw: number, max: string) => `vključena (${perKw} €/kW, do ${max})`,
    subsidyOff: 'ni vključena (primerjava s subvencijo je v tabeli scenarijev)',
    degradation: 'Letno zmanjšanje moči panelov',
    year: 'leto',
    om: 'Letno vzdrževanje in zavarovanje',
    ofInvest: 'vrednosti',
    inverter: 'Menjava razsmernika',
    inverterVal: (y: number, perKwp: string, min: string) => `v ${y}. letu delovanja (${perKwp} €/kWp, najmanj ${min} €)`,
    battery: 'Predpostavka za baterijski hranilnik',
    batteryVal: (eurKwh: string, share: string, cover: string, y: number) => `kapaciteta 1 kWh na 1.000 kWh porabe (5–15 kWh) po ${eurKwh} €/kWh; zviša lastno porabo na ${share} (do ${cover} pokritosti); menjava v ${y}. letu`,
    prices: 'Rast cen elektrike',
    pricesVal: 'izračun v današnjih cenah (brez inflacije)',
    discount: 'Diskontna obrestna mera',
    loanP: 'Predpostavka kredita',
    loanVal: (y: number, r: string) => `na neto znesek, doba ${y} let, obrestna mera ${r} letno`,
    p90: 'Vremenska nihanja med leti',
    p90Val: (p: string) => `v slabšem sončnem letu do ~${p} manj od povprečja`,
    cashTitle: (sc: string) => `Pregled prihrankov in stroškov po letih (€, ${sc})`,
    cashCols: ['Leto', 'Proizvodnja (kWh)', 'Prihranek (€)', 'Vzdrževanje / menjava (€)', 'Čisti letni izkupiček (€)', 'Skupaj stanje (€)'],
    cashNote: (npv: string, irr: string) => `Neto sedanja vrednost (pri 3 % obrestni meri): ${npv} € · Letna donosnost: ${irr}. Zeleno obarvana vrstica označuje leto, ko se investicija v celoti povrne.`,
    note: 'Izračun je informativen in ne nadomešča uradne ponudbe po ogledu objekta.',
    reset: 'Ponastavi na privzete vrednosti',
  },

  // documents for subsidy and loan applications
  docs: {
    kind: 'Predinvesticijska ocena osončenosti in sončne elektrarne',
    id: 'Št. poročila',
    issued: 'Datum izdaje',
    applicant: 'Investitor / vlagatelj',
    address: 'Naslov',
    issuer: 'Izdajatelj',
    issuerVal: (contact: string) => `Sončnik, ${contact}`,
    verify: 'Preverjanje',
    verifyVal: 'Poročilo se iz javnih podatkov vedno izračuna enako na povezavi:',
    blank: '______________________________',
    title: 'Za vlogo za subvencijo in kredit',
    gives: 'To poročilo prikaže lego, strešine, ocenjeno moč, letno in mesečno proizvodnjo, potrebno priključno moč ter oceno naložbe in prihranka s predpostavkami.',
    needTitle: 'Vloge običajno zahtevajo še:',
    need: [
      'ponudbo ali predračun izvajalca z nazivno močjo modulov in razsmernika ter ceno,',
      'soglasje za priključitev elektrodistributerja (samooskrba),',
      'dokazilo o lastništvu (zemljiška knjiga) ali soglasje lastnika,',
      'izpolnjene obrazce aktualnega poziva (Borzen JP-SO-02 za subvencijo, Eko sklad ali banka za kredit).',
    ],
    lidar: 'Model površja je iz let 2014–15; drevesa in novejše stavbe naj izvajalec preveri na ogledu.',
    page: 'Stran',
  },

  // email me the report (PDF)
  reportPdf: {
    title: 'Poročilo v PDF na e-pošto',
    lead: 'Celotno poročilo v PDF (za izvajalca, banko ali vlogo za subvencijo) vam pošljemo na e-pošto, skupaj s povezavo do interaktivnega poročila.',
    submit: 'Pošlji PDF',
    sending: 'Pripravljam PDF …',
    slow: 'Priprava PDF traja do ene minute.',
    ok: 'Poslano. PDF je v vašem e-poštnem predalu (preverite tudi vsiljeno pošto).',
    okLink: 'Poslali smo povezavo do poročila. Preverite e-pošto.',
    marketing: 'Občasno mi pošljite nasvete o sončnih elektrarnah (neobvezno).',
    gateTitle: 'Poročilo v PDF prejmete po e-pošti',
    gateText: 'Tiskanje iz brskalnika ni na voljo. V poročilu kliknite »PDF na e-pošto« in vpišite svoj e-poštni naslov, PDF vam pošljemo v nekaj trenutkih.',
    preparedFor: (email: string, date: string) => `Pripravljeno za ${email}, ${date}`,
    applicantTitle: 'Za vlogo ali banko (neobvezno)',
    applicantName: 'Ime in priimek investitorja',
    applicantAddress: 'Naslov investitorja',
    applicantNote: 'Izpišemo ju v glavi PDF; ne shranjujemo ju.',
  },

  // sources (SPEC §6.8)
  sources: {
    title: 'Metoda, viri in omejitve',
    summary: 'Kako je poročilo izračunano',
    geomH: 'Geometrija.',
    geom: 'ARSO lasersko skeniranje 2014–15: klasificiran oblak točk GKOT, preračunan v 1 m model površja (najvišja točka v celici, z oznako vegetacije) in DMR1 za teren. Parcele, tlorisi stavb in višine iz katastra nepremičnin GURS. Lokalno obzorje je izračunano za vsako celico v 90 smereh do 294 m, dlje pa dopolnjeno s PVGIS obzorjem iz DEM za 1 km list.',
    radH: 'Sevanje.',
    rad: 'PVGIS 5.3 tipično meteorološko leto (SARAH-3 + ERA5, 2005–2023), urni podatki iz najbližje točke 5 km mreže. Sevanje na nagnjene ploskve po modelu Hay–Davies, z upoštevanjem senc vsako uro, deleža vidnega neba in odboja (albedo 0,2). Proizvodnja PV je izračunana iz urnega sevanja na strešino z upoštevanjem temperature modulov (NOCT 45 °C, −0,4 %/°C), 14 % sistemskih izgub ter 3 % izgub zaradi odboja in umazanije; moč je ocenjena kot 0,2 kWp na m² za 70 % površine strešine.',
    limH: 'Omejitve.',
    lim: 'LiDAR je iz let 2014–15: drevesa so od takrat zrasla ali bila posekana, novejših objektov ni. Snemanje je bilo večinoma brez listja, zato so poletne sence listavcev v modelu prej podcenjene, zimske pa precenjene. Ocena proizvodnje je za okoli 6 % nižja od PVGIS PVcalc za enako ploskev (tipično leto ima manj sevanja od dolgoletnega povprečja), zato je konzervativna. Dimniki, antene in strešna okna pod 1 m niso zajeti; vzporedne strešine iste smeri so združene.',
    grid2m: 'Parcela je velika, zato je izračun narejen v mreži 2 m namesto 1 m.',
    attribution: 'Podatki: © ARSO, © GURS, © Evropska komisija JRC – PVGIS.',
    disclaimer: 'Poročilo je informativno in ne nadomešča projektne dokumentacije ali ogleda na terenu.',
  },

  // 3D (SPEC §7)
  three: {
    title: '3D model s sencami',
    loading: 'Nalagam 3D pogled …',
    area: 'Območje',
    grid: 'Mreža',
    heights: 'Višine',
    shadowNote: 'Senca',
    shadowVal: 'simulirana za izbrani trenutek',
    date: 'Datum',
    time: 'Ura (lokalni čas)',
    today: 'danes',
    play: '▶ Predvajaj dan',
    stop: '❚❚ Ustavi',
    now: 'Zdaj',
    elev: 'Višina sonca',
    az: 'Azimut',
    above: 'sonce nad obzorjem',
    below: 'sonce pod obzorjem',
    hint: 'vleci: vrti · desni klik / dva prsta: premik · kolesce: zoom',
    label: (st: number) => bNom(st),
    cap: 'Površje (teren, strehe, krošnje) je obarvano z letnim sevanjem. Črtkana rumena črta je meja parcele. Sence računa brskalnik iz trenutnega položaja sonca, zato kažejo, kje bo senca ob izbranem datumu in uri. Drevesa so prikazana kot poln volumen, pozimi brez listja v resnici prepuščajo več svetlobe.',
    months: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'avg', 'sep', 'okt', 'nov', 'dec'],
    compass: ['S', 'SV', 'V', 'JV', 'J', 'JZ', 'Z', 'SZ'],
    snapshotAlt: '3D pogled parcele',
    printShadow: (date: string, time: string) => `${date} ob ${time}`,
    capPrint: (ortho: boolean, date: string, time: string) => `Površje ${ortho ? 'je prekrito z ortofoto posnetkom (sence na posnetku so od dne snemanja)' : 'je obarvano z letnim sevanjem'}, črtkana rumena črta je meja parcele. Sence so prikazane za ${date} ob ${time}. Drevesa so prikazana kot poln volumen, pozimi brez listja v resnici prepuščajo več svetlobe.`,
    surface: 'Površje',
    ortho: 'Ortofoto',
    orthoLoading: 'nalagam …',
    orthoErr: 'ortofoto ni dosegljiv',
    orthoSrc: 'Ortofoto DOF025 © GURS',
    capOrtho: 'Površje (teren, strehe, krošnje) je prekrito z ortofoto posnetkom. Črtkana rumena črta je meja parcele. Sence računa brskalnik iz trenutnega položaja sonca, zato kažejo, kje bo senca ob izbranem datumu in uri; sence na samem posnetku so od dne snemanja. Drevesa so prikazana kot poln volumen, pozimi brez listja v resnici prepuščajo več svetlobe.',
  },

  // planned new house (builder)
  plan: {
    nav: 'Novogradnja',
    title: 'Novogradnja',
    introTitle: 'Gradite novo hišo?',
    intro: 'Postavite načrtovano hišo na parcelo, primerjajte različice in poiščite postavitev z največ sonca na strehi, fasadah in vrtu. Upoštevamo teren, sosednje stavbe in drevesa ter pokažemo, koliko sonca hiša vzame sosedom.',
    open: 'Postavi hišo na parcelo',
    loading: 'Nalagam urejevalnik …',
    loadingData: 'Nalagam teren in sosednje stavbe …',
    dataFail: 'Podatkov za načrtovanje ni bilo mogoče naložiti.',
    variant: (id: string) => `Različica ${id}`,
    variants: 'Različice',
    add: '+ Nova',
    copy: 'Kopiraj',
    remove: 'Odstrani',
    house: 'Hiša',
    shape: 'Tloris',
    shapes: { rect: 'pravokoten', L: 'v obliki L' },
    len: 'Dolžina',
    wid: 'Širina',
    wingLen: 'Krilo: izstop',
    wingWid: 'Krilo: širina',
    wingSide: 'Krilo',
    sides: { 1: 'desno', '-1': 'levo' } as Record<string, string>,
    eaves: 'Kap nad pritličjem',
    roof: 'Streha',
    roofs: { gable: 'dvokapnica', hip: 'štirikapnica', shed: 'enokapnica', flat: 'ravna' } as Record<string, string>,
    pitch: 'Naklon',
    floor: 'Pritličje nad terenom',
    az: 'Smer slemena',
    azHint: 'azimut, 90° = sleme V–Z, strešini proti J in S',
    rotEW: 'V–Z',
    rotNS: 'S–J',
    rotEdge: 'po robu parcele',
    rotContour: 'po plastnicah',
    site: 'Parcela',
    clearTrees: 'Drevesa na parceli odstranim',
    demolish: (st: number) => `Stavbo ${st} porušim`,
    canvasLabel: 'Tloris parcele z načrtovano hišo',
    canvasHint: 'Povlecite hišo ali kliknite, kam naj gre. Tipke: puščice premik, Q / E zasuk.',
    footprint: 'Tloris',
    height: 'Višina nad terenom',
    clearanceLbl: 'Najmanjši odmik',
    m: (v: string) => `${v} m`,
    m2: (v: string) => `${v} m²`,
    // rules
    rulesTitle: 'Prostorski pogoji (OPN)',
    rulesLead: 'Vpišite pogoje iz občinskega prostorskega načrta za vašo enoto urejanja prostora (najdete jih v PIS ali na občini). Preverimo jih na tej geometriji; to ni uradna presoja skladnosti.',
    setback: 'Odmik od meje (m)',
    maxHeight: 'Največja višina (m)',
    pitchMin: 'Naklon od (°)',
    pitchMax: 'Naklon do (°)',
    maxFz: 'Faktor zazidanosti',
    ridgeRule: 'Sleme',
    ridgeRules: { free: 'prosto', contour: 'vzporedno s plastnicami', long: 'po daljši stranici' } as Record<string, string>,
    noLimit: 'brez',
    checks: {
      inside: (v: string) => `Hiša v celoti na parceli (odmik ${v} m)`,
      setback: (v: string, l: string) => `Odmik od meje ${v} m, zahtevano ${l} m`,
      height: (v: string, l: string) => `Višina nad terenom ${v} m, največ ${l} m`,
      pitch: (v: string, lo: string, hi: string) => `Naklon ${v}°, dovoljeno ${lo}–${hi}°`,
      fz: (v: string, l: string) => `Faktor zazidanosti ${v}, največ ${l}`,
      ridge: (v: string, l: string) => `Sleme ${v}°, zahtevano ${l}°`,
    },
    contourNone: 'Teren je skoraj raven, smer plastnic ni določena.',
    // compute
    compute: 'Izračunaj',
    computeAll: 'Izračunaj vse',
    computing: (pct: string) => `Računam … ${pct} %`,
    stale: 'spremenjeno',
    failed: 'Izračun ni uspel.',
    optimize: 'Poišči najboljšo postavitev',
    optimizing: (pct: string) => `Iščem postavitev … ${pct} %`,
    weights: 'Najpomembnejše mi je',
    weightNames: { balanced: 'vse enako', pv: 'sončna elektrarna', passive: 'zimsko sonce v hiši', garden: 'sonce na vrtu' } as Record<string, string>,
    optDone: (n: number, feasible: number, s: string) => `Preverjenih ${n} postavitev (${feasible} ustreza pogojem) v ${s} s. Najboljše so dodane kot nove različice.`,
    optNone: 'Nobena postavitev te hiše ne izpolnjuje pogojev. Zmanjšajte hišo ali odmik.',
    optModel: 'Iskanje postavitve deluje za parametrično hišo, ne za uvožen model.',
    suggestion: (i: number) => `P${i}`,
    // compare
    compareTitle: 'Primerjava različic',
    cols: ['Različica', 'Elektrarna', 'Strešina', 'Fasada okt–mar', 'Fasada 21. 12.', 'Vrt letno', 'Vrt 21. 3.', 'Sosedje', 'Pogoji'],
    colUnits: ['', 'kWp · MWh/leto', 'kWh/m²', 'kWh/m²', 'ure', 'kWh/m²', 'ure', 'streha / tla', ''],
    compareCap: 'Elektrarna: največja smiselna na novi hiši. Fasada: najbolj osončena stran v kurilni sezoni (pasivni dobitki skozi okna). Vrt: odprte površine parcele po gradnji. Sosedje: največja izguba letnega sevanja na sosednji strehi in površina sosednjih tal, ki 21. 12. izgubi vsaj uro sonca.',
    show: 'Poročilo',
    shown: 'prikazano',
    existing: 'Obstoječe stanje',
    backExisting: 'Prikaži obstoječe stanje',
    // import
    importLbl: 'Uvozi 3D model',
    importHint: 'IFC (ArchiCAD, Revit …), glTF/GLB ali OBJ. Model ostane v vašem brskalniku in se ne shrani v povezavo.',
    importing: 'Berem model …',
    importFail: (e: string) => `Modela ni bilo mogoče prebrati: ${e}`,
    units: 'Enote',
    upAxis: 'Navzgor',
    modelName: (name: string, n: string) => `${name} · ${n} trikotnikov`,
    toParam: 'Nazaj na parametrično hišo',
    modelNotShared: 'Različice z uvoženim modelom niso v povezavi.',
    // report banner
    banner: (id: string) => `Poročilo za različico ${id} z načrtovano hišo`,
    bannerLine: (fp: string, h: string, roof: string) => `Tloris ${fp} m², višina ${h} m nad terenom, ${roof}. Drevesa in porušene stavbe so odstranjeni po nastavitvah različice.`,
    terrainEst: 'Za to območje ni modela terena (DMR), zato je teren pod odstranjenimi drevesi in hišo ocenjen iz okolice.',
    // façades
    facadeTitle: 'Sonce na fasadah',
    facadeCols: ['Fasada', 'Azimut', 'm²', 'kWh/m²·leto', 'okt–mar', 'brez senc okt–mar', '21. 12.', '21. 3.', '21. 6.', 'Izguba senc'],
    facadeCap: 'Sevanje na navpično steno (okna) po straneh neba, s sencami terena, dreves, sosednjih stavb in same hiše. Okt–mar je kurilna sezona: več sonca pomeni več brezplačne toplote skozi okna. Ure so direktno sonce ob jasnem dnevu. Napušči, balkoni in senčila niso upoštevani.',
    sectors: ['severna', 'severovzhodna', 'vzhodna', 'jugovzhodna', 'južna', 'jugozahodna', 'zahodna', 'severozahodna'],
    // neighbours
    impactTitle: 'Vpliv na sosede',
    impactCols: ['Stavba', 'Streha prej', 'po gradnji', 'Sprememba', 'Ure 21. 12.', 'Ure 21. 3.'],
    impactCap: 'Primerjava z današnjim stanjem: izguba (rdeče) ali pridobitev sonca (modro) na sosednjih strehah in tleh v pasu 40 m okoli hiše. Upošteva tudi posek dreves na vaši parceli. Strehe: letno sevanje v kWh/m² in povprečna sprememba ur direktnega sonca.',
    impactGround: (m2: string, max: string) => `Na sosednjih odprtih tleh ${m2} m² 21. 12. izgubi vsaj uro sonca (največ ${max} h).`,
    impactGroundNone: 'Sosednja odprta tla 21. 12. ne izgubijo niti ure sonca.',
    impactNone: 'V pasu 40 m okoli hiše ni sosednjih stavb.',
    impactDays: { '21. 12.': '21. 12.', '21. 3.': '21. 3.', '21. 6.': '21. 6.' } as Record<string, string>,
    impactLegendLost: 'izgubljene ure',
    impactLegendGain: 'pridobljene ure',
    impactAlt: 'Karta spremembe ur sonca okoli načrtovane hiše',
  },

  footer: {
    version: (v: string) => `Različica podatkov: ${v}`,
    app: (v: string) => `Aplikacija ${v}`,
  },

  // site shell, landing page and static pages
  site: {
    tagline: 'Študija osončenosti parcele',
    metaTitle: 'Sončnik – koliko sonca dobi vaša parcela in streha',
    metaDescription: 'Brezplačna študija osončenosti za vašo parcelo: karta sonca in senc v 1 m mreži, ure sonca po letnih časih, 3D model senc in ocena sončne elektrarne na strehi. V nekaj sekundah, brez registracije.',
    skip: 'Preskoči na vsebino',
    nav: { demo: 'Primer', audience: 'Za koga', how: 'Kako deluje', features: 'Kaj dobite', faq: 'Vprašanja', start: 'Izračunaj', home: 'Domov', newParcel: 'Nova parcela', label: 'Glavna navigacija' },
    hero: {
      eyebrow: 'Študija osončenosti · občina Šentjur',
      title: 'Koliko sonca dobi vaša parcela?',
      lead: 'Sonce in senca na vsakem kvadratnem metru parcele in strehe, ure sonca po letnih časih in ocena sončne elektrarne.',
      trust: ['Brezplačno, brez registracije', 'LiDAR ARSO, mreža 1 m', 'Poročilo takoj'],
      visualAlt: 'Karta letnega sevanja za primer parcele 243/1, k.o. Marija Dobje: južne strešine rdeče, sence dreves modre',
      chipPv: 'na južni strehi',
      chipGround: 'na vrtu na leto',
      sheet: { parcel: 'Parcela', layer: 'Sloj', layerVal: 'Letno sevanje', ramp: 'kWh/m²·leto' },
    },
    search: {
      title: 'Poiščite parcelo',
      tabsLabel: 'Način iskanja parcele',
      tabAddress: 'Naslov',
      tabParcel: 'Parcelna številka',
      tabMap: 'Karta',
      hintAddress: 'Vpišite hišno številko v občini Šentjur. Poiskali bomo parcelo pod naslovom.',
      hintParcel: 'Številko parcele najdete v zemljiškoknjižnem izpisku ali v javnem vpogledu v nepremičnine.',
      hintMap: 'Približajte se in kliknite parcelo.',
      examples: 'Poskusite:',
    },
    waitlist: {
      title: 'Vaša občina še ni pokrita?',
      lead: 'Sončnik dodaja nova območja. Pustite e-pošto in sporočili vam bomo, ko bo vaša občina na voljo.',
      obcina: 'Občina',
      obcinaPlaceholder: 'npr. Celje',
    },
    demo: {
      eyebrow: 'Primer poročila',
      title: 'Hiša v Marija Dobje: kje je sonce in koliko elektrarne prenese streha',
      lead: 'Parcela 243/1 v k.o. Marija Dobje ima tri stavbe, sadovnjak in gozd na severni strani. Takšno poročilo dobite za katerokoli parcelo v občini Šentjur.',
      mapCap: 'Letno sevanje v 1 m mreži: južne strešine so rdeče, sence dreves modre.',
      threeCap: '3D model s sencami za izbrani dan in uro.',
      open: 'Odpri celotno poročilo',
      more: 'Preizkusite še',
    },
    features: {
      eyebrow: 'Kaj dobite',
      title: 'Celotna študija osončenosti, ne le en podatek',
      items: [
        { h: 'Karta sonca v 1 m mreži', p: 'Letno sevanje za vsak kvadratni meter parcele, strehe in krošnje, z upoštevanjem naklona, smeri in senc.' },
        { h: 'Ure sonca po letnih časih', p: 'Koliko ur direktnega sonca dobi vsak del vrta 21. decembra, ob enakonočju in poleti.' },
        { h: 'Pot sonca in obzorje', p: 'Kje sonce vzhaja in zahaja in kdaj ga zakrijejo hrib, sosednja hiša ali drevo. Za katerokoli točko na karti.' },
        { h: 'Strehe in sončna elektrarna', p: 'Vsaka strešina posebej: površina, naklon, azimut, izguba senc ter ocena moči in letne proizvodnje.' },
        { h: '3D model s sencami', p: 'Izberite datum in uro ali predvajajte cel dan in poglejte, kam pade senca.' },
        { h: 'PDF na e-pošto, deli', p: 'Poročilo v PDF prejmete na e-pošto ali delite povezavo z izvajalcem, arhitektom ali sosedom.' },
      ],
    },
    how: {
      eyebrow: 'Kako deluje',
      title: 'Tri koraki, približno deset sekund',
      steps: [
        { h: 'Poiščite parcelo', p: 'Po naslovu, parcelni številki ali s klikom na karti.' },
        { h: 'Sončnik izračuna', p: 'Upošteva teren, sosednje stavbe in drevesa ter pot sonca skozi vse leto.' },
        { h: 'Preberite poročilo', p: 'Karte, grafi, strehe in 3D model. Povezava vedno vrne enako poročilo.' },
      ],
    },
    audience: {
      eyebrow: 'Za koga',
      title: 'Za lastnike, arhitekte in investitorje',
      lead: 'Isti natančen izračun odgovori na različna vprašanja: od sončne elektrarne na domači strehi do umestitve novega objekta na zemljišče.',
      items: [
        { tag: 'Lastniki hiš', h: 'Se elektrarna na moji strehi splača?', p: 'Katera strešina dobi največ sonca, koliko kWp prenese in v koliko letih se naložba povrne. Pa tudi, kje bo vrt dobil dovolj sonca.',
          points: ['Ocena moči, proizvodnje in prihranka', 'Ure sonca za vrt po letnih časih', 'PDF za pogovor z izvajalcem'] },
        { tag: 'Arhitekti in projektanti', h: 'Osončenost že v idejni zasnovi', p: 'Obzorje, pot sonca in sence sosednjih stavb, dreves in terena za katerokoli točko parcele, v mreži 1 m.',
          points: ['Diagram poti sonca z obzorjem za izbrano točko', 'Ure sonca 21. 12., 21. 3. in 21. 6.', 'Strešine z naklonom, azimutom in izgubo senc'] },
        { tag: 'Investitorji in razvijalci', h: 'Preverite zemljišče pred nakupom', p: 'Kateri deli zemljišča imajo sonce tudi pozimi in kje hrib, gozd ali sosednja gradnja skrajšajo sončni dan.',
          points: ['Karta letnega sevanja za celo parcelo', 'Primerjava več parcel v nekaj minutah', 'Povezava, ki vedno vrne isto poročilo'] },
      ],
    },
    data: {
      eyebrow: 'Zakaj Sončnik',
      title: 'Natančno, hitro in brezplačno',
      stats: [
        { v: '1 m²', l: 'natančnost karte sonca in senc' },
        { v: '365 dni', l: 'sonce in sence za vsako uro v letu' },
        { v: '~10 s', l: 'do celotnega poročila' },
        { v: '0 €', l: 'brez registracije in obveznosti' },
      ],
    },
    faq: {
      eyebrow: 'Vprašanja',
      title: 'Pogosta vprašanja',
      items: [
        { q: 'Ali je Sončnik res brezplačen?', a: 'Da. Poročilo je brezplačno in ne potrebuje registracije. Ponudbo za sončno elektrarno lahko zahtevate, ni pa obvezno.' },
        { q: 'Katero območje je pokrito?', a: 'Trenutno občina Šentjur (pilotno območje). Če vaša parcela leži drugje, pustite e-pošto in obvestili vas bomo, ko bo območje dodano.' },
        { q: 'Kako natančen je izračun?', a: 'Karta ima ločljivost 1 m, sence so izračunane za vsako uro v letu. Ocena proizvodnje elektrarne je namenoma konzervativna. Za projekt elektrarne je še vedno potreben ogled izvajalca.' },
        { q: 'Kako sveži so podatki o drevesih in stavbah?', a: 'Model površja je iz let 2014–15. Drevesa so lahko od takrat zrasla ali bila posekana, novejših stavb v modelu ni. Na karti dreves so označene krošnje, ki jih je smiselno preveriti na terenu.' },
        { q: 'Kje najdem številko parcele?', a: 'Najlažje poiščete parcelo po naslovu ali jo kliknete na karti. Številka je zapisana tudi v zemljiškoknjižnem izpisku in v javnem vpogledu v podatke o nepremičninah (GURS).' },
        { q: 'Kaj se zgodi z mojimi podatki?', a: 'Izračun teče v vašem brskalniku. Ne potrebujemo računa in ne uporabljamo piškotkov. Kontaktne podatke posredujemo izvajalcem le, če sami pošljete povpraševanje za ponudbo.' },
        { q: 'Ali lahko poročilo uporabim za gradbeno dovoljenje?', a: 'Ne. Poročilo je informativno in ne nadomešča projektne dokumentacije ali ogleda na terenu.' },
      ],
    },
    final: {
      title: 'Preverite svojo parcelo',
      lead: 'Brezplačno, brez registracije, v približno deset sekundah.',
      button: 'Poišči parcelo',
    },
    footer: {
      about: 'Sončnik izračuna sonce in senco na parceli iz javnih podatkov. Vse se računa v vašem brskalniku.',
      coverage: 'Pokrito območje: občina Šentjur.',
      privacy: 'Zasebnost',
      contact: 'Kontakt',
      attribution: 'Podatki: © ARSO, © GURS, © Evropska komisija JRC – PVGIS.',
    },
    privacy: {
      title: 'Zasebnost',
      metaDescription: 'Kako Sončnik ravna z vašimi podatki: izračun v brskalniku, brez piškotkov in brez računa.',
      updated: 'Posodobljeno: oktober 2026',
      operator: (who: string) => `Upravljavec: ${who}.`,
      sections: [
        { h: 'Izračun v vašem brskalniku', p: 'Poročilo se izračuna v vašem brskalniku. Brskalnik prenese javne podatke (model površja, kataster, vremenske podatke) za okolico izbrane parcele s strežnika s podatki. Strežnik pri tem, kot vsak spletni strežnik, za kratek čas beleži tehnične podatke zahtevka, na primer naslov IP. Katero parcelo ste izbrali, ne shranjujemo.' },
        { h: 'Brez piškotkov in računa', p: 'Sončnik ne uporablja piškotkov in ne potrebuje registracije. Če je vklopljena statistika obiska (Plausible Analytics), ta deluje brez piškotkov in brez osebnih podatkov; beleži le skupno število obiskov in dogodkov, kot je »poročilo izračunano«.' },
        { h: 'Povpraševanje za ponudbo', p: 'Če izpolnite obrazec za ponudbo, pošljemo vaše ime, e-pošto, neobvezno telefon in opombo ter povzetek poročila (parcela, najboljša strešina, ocena moči in prihranka) izbranim izvajalcem sončnih elektrarn, da vam pripravijo ponudbo. Pravna podlaga je vaša privolitev, ki jo lahko kadarkoli prekličete. Podatke hranimo, dokler je to potrebno za posredovanje ponudbe, nato jih izbrišemo.' },
        { h: 'Pošiljanje poročila', p: 'Če si poročilo pošljete na e-pošto, naslov uporabimo za to sporočilo in ga izpišemo v nogi PDF. Če vpišete ime in naslov investitorja, ju izpišemo v glavi PDF in ju ne shranjujemo. PDF pripravimo na strežniku (Cloudflare) in ga ne hranimo. Če označite, da želite nasvete, vam jih občasno pošljemo; odjava je mogoča v vsakem sporočilu ali s sporočilom na spodnji naslov.' },
        { h: 'Zaščita pred zlorabo', p: 'Obrazce ščiti Cloudflare Turnstile, ki preveri, da jih ne pošilja robot. Za omejitev števila pošiljanj hranimo le skrajšan zgoščen zapis naslova IP, ne samega naslova. E-pošto pošiljamo prek ponudnika Resend.' },
        { h: 'Obvestilo o novih območjih', p: 'Če pustite e-pošto za obvestilo, jo uporabimo le za eno sporočilo, ko bo vaše območje pokrito, nato jo izbrišemo.' },
        { h: 'Karta', p: 'Ko odprete karto za izbiro parcele, brskalnik naloži podlago s strežnikov OpenFreeMap, ki pri tem prejmejo vaš naslov IP. 3D pogled naloži ortofoto posnetek okolice parcele s strežnika Geodetske uprave RS (ipi.eprostor.gov.si), ki prav tako prejme vaš naslov IP.' },
        { h: 'Vaše pravice', p: 'Imate pravico do dostopa, popravka in izbrisa svojih podatkov ter do preklica privolitve. Pišite nam na spodnji naslov. Pritožbo lahko vložite pri Informacijskem pooblaščencu RS.' },
      ],
      contact: (email: string) => `Kontakt: ${email}`,
      back: 'Nazaj na Sončnik',
    },
    notFound: {
      title: 'Strani ni mogoče najti',
      lead: 'Povezava je napačna ali stran ne obstaja več.',
      back: 'Na začetno stran',
    },
    crash: {
      title: 'Nekaj je šlo narobe',
      lead: 'Prikaza ni bilo mogoče dokončati. Osvežite stran ali poskusite z drugo parcelo.',
      reload: 'Osveži stran',
    },
  },

  // report extras
  verdict: {
    excellent: 'Odlična lega za sončno elektrarno',
    good: 'Primerna lega za sončno elektrarno',
    poor: 'Strehe niso primerne za sončno elektrarno',
    noBuildings: 'Parcela brez stavb',
    pvLine: (kwp: string, kwh: string, smerLoc: string, st: number) => `Na ${smerLoc} strešini ${bGen(st)} je prostora za ~${kwp} kWp sončno elektrarno, ki bi letno proizvedla ~${kwh} kWh elektrike.`,
    pvLineMatched: (kwp: string, kwh: string, smerLoc: string, st: number, fullKwp: string) => `Za vašo porabo zadostuje ~${kwp} kWp sončna elektrarna (~${kwh} kWh/leto) na ${smerLoc} strešini ${bGen(st)} (na strešini je prostora do ~${fullKwp} kWp).`,
    pvLineSelected: (kwp: string, kwh: string, planes: string) => `Na izbranih strešinah (${planes}) je prostora za ~${kwp} kWp sončno elektrarno, ki bi letno proizvedla ~${kwh} kWh elektrike.`,
    poorLine: 'Nobena strešina ne dobi dovolj sonca na dovolj veliki površini. Poglejte karto za vrt in odprte površine.',
    groundLine: (pct: string) => `Vrt in odprte površine parcele prejmejo v povprečju ${pct} % sonca popolnoma odprte lege.`,
  },
  sections: { label: 'Poglavja poročila', plan: 'Novogradnja', facades: 'Fasade', impact: 'Sosedje', summary: 'Povzetek', map: 'Karta', sunpath: 'Pot sonca', monthly: 'Sonce na vrtu', roofs: 'Strehe in elektrarna', three: '3D sence', sources: 'Metoda' },
  share: { share: 'Deli', shareText: (title: string) => `Študija osončenosti: ${title}` },
  progressNote: 'Vse se računa v vašem brskalniku. Običajno traja 5–15 sekund.',
  progressTipsTitle: 'Ali ste vedeli?',
  progressTips: [
    'Strešina proti jugu z naklonom 30–40° dobi v Sloveniji največ sonca na leto.',
    'Vzhodna in zahodna strešina dobita približno petino manj sonca kot južna, a bolj enakomerno čez dan.',
    'Pozimi je sonce opoldne v Šentjurju le okoli 20° nad obzorjem, zato senco mečejo že nizka drevesa.',
    '1 kWp sončne elektrarne v Sloveniji letno proizvede okoli 1.000–1.200 kWh elektrike.',
    'Elektrika, ki jo porabite doma, je vredna precej več kot elektrika, ki jo oddate v omrežje.',
  ],
};

export type Strings = typeof sl;
